import React, { useState } from 'react';
import styles from './getintouchform.module.scss';
import { allCountries } from '@enums/countries';
import { environment } from '@environment';
import { useTranslation } from 'next-i18next';

interface IGetInTouchForm {
	firstName: string;
	lastName: string;
	email: string;
	company: string;
	phone: string;
	country: string;
	source: string;
	sourceOther?: string;
}

type Props = {
	product?: string;
};

function GetInTouchForm({ product }: Props) {
	const { t } = useTranslation();
	const [formData, setFormData] = useState<IGetInTouchForm>({
		firstName: '',
		lastName: '',
		email: '',
		company: '',
		phone: '',
		country: '',
		source: '',
		sourceOther: ''
	});
	const [isLoading, setIsLoading] = useState(false);
	const [invalidFields, setInvalidFields] = useState<string[]>([]);

	const validationMessages = {
		firstName: t('Minimum 3 characters required'),
		lastName: t('Minimum 3 characters required'),
		email: t('Wrong email format'),
		company: t('Minimum 3 characters required'),
		phone: t('invalid_phone_number'),
		country: t('Minimum 2 characters required')
	};

	const sourceOptions = [
		{ name: t('advertise_source_search'), id: 0 },
		{ name: t('advertise_source_referral'), id: 1 },
		{ name: t('adverstive_source_anghami_ads'), id: 2 },
		{ name: t('advertise_source_linkedin'), id: 3 },
		{ name: t('advertise_source_artist'), id: 4 },
		{ name: t('advertise_source_other'), id: 5 }
	];

	const handleSubmit = (event) => {
		event.preventDefault();

		setInvalidFields([]);

		const validationRegex = {
			firstName: /^.{3,}$/, // Minimum 3 characters
			lastName: /^.{3,}$/, // Minimum 3 characters
			email: /^\S+@\S+\.\S+$/, // Email format
			company: /^.{3,}$/, // Minimum 3 characters
			phone: /^\+*[0-9]{7,}$/, // At least 7 digits, can start with '+'
			country: /^.{2,}$/ // Minimum 2 characters
		};

		const invalidFields: string[] = [];

		for (const field in formData) {
			if (validationRegex[field] && !validationRegex[field].test(formData[field])) {
				invalidFields.push(field);
			}
		}

		if (invalidFields.length > 0) {
			setInvalidFields(invalidFields);
			return;
		}

		setIsLoading(true);

		const body = new URLSearchParams();
		body.append('type', 'GETbrandpage');
		body.append('firstname', formData.firstName);
		body.append('lastname', formData.lastName);
		body.append('email', formData.email);
		body.append('company', formData.company);
		body.append('country', formData.country);
		body.append('phone', formData.phone);
		body.append('source', formData.source);
		if (product) {
			body.append('product', product);
		}

		fetch(environment.gateway_prod_endpoint, {
			method: 'POST',
			body: body.toString(),
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded'
			}
		})
		.then(async (response) => {
			setIsLoading(false);

			if (!response.ok) {
				throw new Error('An error has occurred. Please try again!');
			} else {
				return response.json();
			}
		})
		.then((data) => {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const responseData = {
				...data,
				title: data.message || 'Thank you for submitting the form!',
				displaymode: 'toast'
			};
				// TODO: handle response here
				// console.log(responseData);
		})
		.catch((error) => {
			setIsLoading(false);
			throw error;
		});
	};

	const handleChange = (event) => {
		const { name, value } = event.target;
		setFormData((prevFormData) => ({
			...prevFormData,
			[name]: value
		}));
	};

	return (
		<div className={styles.sectionContainer} id="advertise_getintouch">
			<div className={styles.sectionTitle}>{t('advertise_get_in_touch_title')}</div>
			{product === 'advertise' && (
				<div className={styles.sectionDescription}>{t('advertise_get_in_touch_subtitle')}</div>
			)}

			<form className={styles.form}>
				<div className={styles.coupledQuestions}>
					<div className={styles.column}>
						<input
							type="text"
							name="firstName"
							placeholder={t('First name')}
							value={formData.firstName}
							className={invalidFields.includes('firstName') ? styles.invalidField : ''}
							onChange={handleChange}
						/>
						{invalidFields.includes('firstName') && <p>{validationMessages.firstName}</p>}
					</div>
					<div className={styles.column}>
						<input
							type="text"
							name="lastName"
							placeholder={t('Last name')}
							value={formData.lastName}
							className={invalidFields.includes('lastName') ? styles.invalidField : ''}
							onChange={handleChange}
						/>
						{invalidFields.includes('lastName') && <p>{validationMessages.lastName}</p>}
					</div>
				</div>
				<div className={styles.coupledQuestions}>
					<div className={styles.column}>
						<input
							type="email"
							name="email"
							placeholder={t('Email')}
							value={formData.email}
							className={invalidFields.includes('email') ? styles.invalidField : ''}
							onChange={handleChange}
						/>
						{invalidFields.includes('email') && <p>{validationMessages.email}</p>}
					</div>
					<div className={styles.column}>
						<input
							type="text"
							name="company"
							placeholder={t('company')}
							value={formData.company}
							className={invalidFields.includes('company') ? styles.invalidField : ''}
							onChange={handleChange}
						/>
						{invalidFields.includes('company') && <p>{validationMessages.company}</p>}
					</div>
				</div>
				<div className={styles.coupledQuestions}>
					<div className={styles.column}>
						<input
							type="tel"
							name="phone"
							placeholder={t('Mobile number')}
							value={formData.phone}
							className={invalidFields.includes('phone') ? styles.invalidField : ''}
							onChange={handleChange}
						/>
						{invalidFields.includes('phone') && <p>{validationMessages.phone}</p>}
					</div>
					<div className={styles.column}>
						<select
							name="country"
							value={formData.country}
							className={invalidFields.includes('country') ? styles.invalidField : ''}
							onChange={handleChange}
						>
							<option value="">{t('Country')}</option>
							{allCountries.map((c) => (
								<option key={c.name} value={c.name}>
									{c.name}
								</option>
							))}
						</select>
						{invalidFields.includes('country') && <p>{validationMessages.country}</p>}
					</div>
				</div>

				<select
					name="source"
					className={styles.dropdown}
					value={formData.source}
					onChange={handleChange}
				>
					<option value="">{t('advertise_source_title')}</option>
					{sourceOptions.map((h) => (
						<option key={h.id} value={h.name}>
							{h.name}
						</option>
					))}
				</select>
				{formData.source === 'Other' && (
					<input
						type="text"
						name="sourceOther"
						placeholder=""
						value={formData.sourceOther}
						className={styles.otherInput}
						onChange={handleChange}
					/>
				)}
				<button type="submit" className={styles.sendButton} onClick={handleSubmit}>
					{isLoading ? 'On the way...✈️' : t('Send')}
				</button>
			</form>
		</div>
	);
}

export default GetInTouchForm;
